<script setup>
import { computed } from 'vue';

import ConfirmButton from 'dashboard/components-next/button/ConfirmButton.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Label from 'dashboard/components-next/label/Label.vue';

import { formatProviderName } from '../../helpers/kraftchatAIFormatters';
import {
  providerFormContent,
  resolveCredentialBadge,
  resolveCredentialMessage,
} from '../../helpers/kraftchatAIProviderConfig';
import KraftchatAIProviderEditForm from './KraftchatAIProviderEditForm.vue';

const props = defineProps({
  credential: {
    type: Object,
    required: true,
  },
  isEditing: {
    type: Boolean,
    default: false,
  },
  isSaving: {
    type: Boolean,
    default: false,
  },
  isDeleting: {
    type: Boolean,
    default: false,
  },
  testState: {
    type: Object,
    default: undefined,
  },
});

const emit = defineEmits([
  'editProvider',
  'deleteProvider',
  'updateProvider',
  'cancelEdit',
  'testProvider',
]);

const isTesting = computed(() => Boolean(props.testState?.testing));

const hasBeenTested = computed(() => {
  return (
    Boolean(props.credential.last_tested_at) || Boolean(props.testState?.result)
  );
});

const testButtonLabel = computed(() => {
  return hasBeenTested.value
    ? providerFormContent.retestLabel
    : providerFormContent.testLabel;
});

const statusBadge = computed(() => {
  return resolveCredentialBadge(props.credential, props.testState);
});

const statusMessage = computed(() => {
  return resolveCredentialMessage(props.credential, props.testState);
});
</script>

<template>
  <article class="rounded-xl border border-n-weak bg-n-background p-4">
    <div v-if="!isEditing" class="flex items-start justify-between gap-3">
      <div class="min-w-0 space-y-1">
        <div class="flex items-center gap-2">
          <h4 class="text-sm font-semibold text-n-slate-12">
            {{ formatProviderName(credential.provider) }}
          </h4>

          <Label
            :label="statusBadge.label"
            :color="statusBadge.color"
            compact
          />
        </div>

        <p class="truncate text-sm text-n-slate-11">
          {{ credential.default_model || providerFormContent.modelFallback }}
        </p>

        <p class="text-sm font-medium text-n-slate-12">
          {{ providerFormContent.currentKeyLabel }}
          {{ credential.masked_api_key }}
        </p>

        <p class="text-sm text-n-slate-11">
          {{ statusMessage }}
        </p>
      </div>

      <div
        class="flex flex-shrink-0 items-center gap-2"
        :class="{ 'pointer-events-none opacity-60': isTesting }"
      >
        <NextButton
          type="button"
          faded
          slate
          size="sm"
          :is-loading="isTesting"
          :disabled="isTesting"
          :label="testButtonLabel"
          @click="emit('testProvider', credential)"
        />

        <NextButton
          type="button"
          faded
          slate
          size="sm"
          :disabled="isTesting"
          :label="providerFormContent.editLabel"
          @click="emit('editProvider', credential)"
        />

        <ConfirmButton
          variant="faded"
          color="ruby"
          confirm-color="ruby"
          size="sm"
          :is-loading="isDeleting"
          :label="providerFormContent.deleteLabel"
          :confirm-label="providerFormContent.confirmDeleteLabel"
          :confirm-hint="providerFormContent.deleteConfirmHint"
          @click="emit('deleteProvider', credential)"
        />
      </div>
    </div>

    <KraftchatAIProviderEditForm
      v-else
      :credential="credential"
      :is-saving="isSaving"
      @update-provider="emit('updateProvider', credential, $event)"
      @cancel-edit="emit('cancelEdit')"
    />
  </article>
</template>
